import {isNativelyFocusableElement, prepareNativeFocusParameters, TElement} from '@platform';
import * as React from 'react';

import * as Errors from '../../../Shared/Constants/Errors';
import areEqualArrays from '../../../Shared/Util/areEqualArrays';
import generateRandomId from '../../../Shared/Util/generateRandomId';
import noop from '../../../Shared/Util/noop';
import {TFocusableNodeType} from '../../../Tree/Constants/FocusableNodeType';
import FocusableNode from '../../../Tree/FocusableNode';
import TreeWorker from '../../../Tree/TreeWorker';
import ContainerContext from '../Contexts/ContainerContext';
import FocusableContext from '../Contexts/FocusableContext';

import FocusableComponentRenderStatus from './Constants/FocusableComponentRenderStatus';
import IUseFocusReturnValue from './Interfaces/IUseFocusReturnValue';
import TUseFocusOptions from './Types/TUseFocusOptions';
import mapUseFocusHookToNode from './Util/mapUseFocusHookToNode';
import UseFocusMutableState from './Util/UseFocusMutableState';

/**
 * A custom hook for making any function component focusable.
 */

type TUseFocus = <T extends TFocusableNodeType>(options?: TUseFocusOptions<T>) => IUseFocusReturnValue;

const useFocus: TUseFocus = <T extends TFocusableNodeType>(
    options: TUseFocusOptions<T> = defaultOptions
): IUseFocusReturnValue => {
    const containerContext = React.useContext(ContainerContext);
    const focusableContext = React.useContext(FocusableContext);
    const focusOnMount = options.focusOnMount ?? false;

    /**
     * A boolean indicating whether or not the component *is* in focus. Triggers a re-render upon
     * change.
     */

    const [isFocused, setIsFocused] = React.useState(false);

    /**
     * A unique, constant and immutable ID for this component's associated focusable node. Created
     * in the the component for debugging/visibility, etc.
     *
     * If a `options.focusableId` is provided, it will be pulled during *first* render only, and can
     * not be changed.
     */

    const focusableId = React.useMemo(() => options.focusableId || generateRandomId(), []);

    /**
     * A long-lived, mutable data store for non-reactive stateful data that should not trigger
     * re-rendering when changed.
     */

    const mutableState = React.useMemo(() => new UseFocusMutableState<TFocusableNodeType>(), []);

    // Shallow-copy the latest props on each render into the `mutableState` to ensure that the
    // `node` always has access to the latest actions and handlers

    Object.assign(mutableState.options, options);

    /**
     * Focuses the component, and calls `focus()` on the underlying focusable element if present
     */

    const focusComponent = () => {
        setIsFocused(true);

        if (focusableContext.config.useNativeFocus && mutableState.focusableElement) {
            const focusParameters = prepareNativeFocusParameters(focusableContext.config.preventScroll);

            mutableState.focusableElement.focus(...focusParameters);
        }
    };

    /**
     * Blurs the component, and calls `blur()` on the underlying focusable element if present
     */

    const blurComponent = () => {
        setIsFocused(false);

        if (focusableContext.config.useNativeFocus && mutableState.focusableElement) {
            mutableState.focusableElement.blur();
        }
    };

    /**
     * An instance of an associated `FocusableNode` created on the *first* render only, and returned
     * by reference on all subsequent renders.
     *
     * Provided with all dependencies necessary to track the state of this component, and trigger
     * focus/blur on the component.
     */

    const node = React.useMemo(
        () =>
            mapUseFocusHookToNode(
                focusableId,
                containerContext.parentContainerId,
                mutableState,
                focusComponent,
                blurComponent,
                options.type
            ),
        []
    );

    // If either the `focusableAnchors` or `focusableBasis` props have changed since the last render,
    // schedule a re-order of the parent node (once all other children have updated).

    if (
        (options.focusableAnchors && !areEqualArrays(options.focusableAnchors, node.anchors)) ||
        (options.focusableBasis && options.focusableBasis !== node.basis)
    ) {
        if (node.parent) TreeWorker.scheduleReorder(node.parent);
    }

    const registerFocusable = React.useMemo(
        () => (node.isRoot ? focusableContext.registerFocusable : containerContext.registerFocusable),
        []
    );

    const unregisterFocusable = React.useMemo(
        () => (node.isRoot ? focusableContext.unregisterFocusable : containerContext.unregisterFocusable),
        []
    );

    /**
     * Handles the underlying focusable DOM element gaining focus via external interaction (e.g.
     * click, tab, etc), and informs the `FocusManager` instance that the node should take focus.
     */

    const handleFocus = React.useCallback(() => focusableContext.handleNativeFocus(node.id), []);

    /**
     * Handles the underlying focusable DOM element losing focus via external interaction (e.g.
     * mouseout, window.blur()), and informs the `FocusManager` instance that the node should be
     * blurred.
     */

    const handleBlur = React.useCallback(() => focusableContext.handleNativeBlur(node.id), []);

    /**
     * Handles click events from the underlying focusable DOM element. Used to trigger "select"
     * behaviour When the `selectOnClick` configuration option is set, allowing the sharing of
     * select handling logic between 5-way AND pointer-based focus management systems.
     */

    const handleClick = React.useCallback(() => focusableContext.handleClick(node.id), []);

    /**
     * Receives a reference to a userland DOM element or native element, adds native focus handlers
     * to it, and updates the component's internal reference.
     */

    const setFocusableElementRef = React.useCallback((element: TElement | null) => {
        if (element === null) return;

        // Duck-type if DOM node rather than native element

        const htmlElementOrNativeElement: any = element;

        if (
            focusableContext.config.useNativeFocus &&
            htmlElementOrNativeElement &&
            typeof htmlElementOrNativeElement.tagName === 'string' &&
            node.isLeaf
        ) {
            // Only apply native focus, blur, hover and click functionality to leaf nodes (not
            // containers).

            if (
                process.env.NODE_ENV !== 'production' &&
                typeof htmlElementOrNativeElement.attributes.tabindex === 'undefined' &&
                !isNativelyFocusableElement(htmlElementOrNativeElement)
            ) {
                console.warn(Errors.TABINDEX_NOT_PRESENT(options.componentDisplayName));
            }

            htmlElementOrNativeElement.addEventListener('focus', handleFocus);
            htmlElementOrNativeElement.addEventListener('blur', handleBlur);

            if (focusableContext.config.focusOnHover) {
                htmlElementOrNativeElement.addEventListener('mouseover', htmlElementOrNativeElement.focus);
                htmlElementOrNativeElement.addEventListener('mouseout', htmlElementOrNativeElement.blur);
            }

            if (focusableContext.config.selectOnClick) {
                htmlElementOrNativeElement.addEventListener('click', handleClick);
            }
        }

        mutableState.focusableElement = htmlElementOrNativeElement;

        if (mutableState.isRenderedPendingMount) {
            // Once a nullable component's element ref has been parsed, we can be sure that the
            // component has been fully mounted. This is therefore the optimum time to register the
            // node.

            registerFocusable(node, focusOnMount);

            mutableState.renderStatus = FocusableComponentRenderStatus.RENDERED;
        }
    }, []);

    /**
     * Handles the outcome of the `withNullCheckRenderSpy` HOC (if used), ensuring that if a
     * userland component returns `null` in some circumstances, then it is treated by the focus
     * manager as if the component it had been unmounted.
     */

    const setComponentWillRender = (componentWillRender: boolean): void => {
        if (componentWillRender && mutableState.isNulled) {
            // Component rendering after a nulled state.

            if (focusableContext.config.useNativeFocus) {
                // Wait until `useLayoutEffect` triggers mounting before registering node.

                mutableState.renderStatus = FocusableComponentRenderStatus.RENDERED_PENDING_MOUNT;
            } else {
                registerFocusable(node, focusOnMount);

                mutableState.renderStatus = FocusableComponentRenderStatus.RENDERED;
            }
        } else if (!componentWillRender) {
            if (mutableState.isRendered) {
                // Remove node from focusable tree

                unregisterFocusable(focusableId);
            }

            mutableState.renderStatus = FocusableComponentRenderStatus.NULLED;
        }
    };

    /**
     * Handles the "mounting" and "unmounting" of the component, ensuring it is registered or
     * unregistered as appropriate. Works closely with the logic defined in
     * `handleConditionalRender` to ensure all lifecycle permutations are accounted for.
     */

    React.useLayoutEffect(() => {
        // If the node is not rendering upon mount, it has been nulled and does not need to be
        // registered.

        if (!mutableState.isRendering) return;

        // If the node is still in a `RENDERING` state after `setComponentWillRender` has been
        // called, then we can be sure it will render, or the component is not nullable.

        // Ensure the node is in a `RENDERED` state before the node is registered incase
        // `focusOnMount` is set. Otherwise, the component's node will not handle the focus action.

        mutableState.renderStatus = FocusableComponentRenderStatus.RENDERED;

        registerFocusable(node, focusOnMount);

        return () => {
            unregisterFocusable(focusableId);

            mutableState.renderStatus = FocusableComponentRenderStatus.STALE;

            // Coerce to `any` and resort to duck typing to satisfy React DOM and React Native
            // builds

            const htmlElementOrNativeElement: any = mutableState.focusableElement;

            if (htmlElementOrNativeElement && typeof htmlElementOrNativeElement.tagName === 'string') {
                htmlElementOrNativeElement.removeEventListener('focus', handleFocus);
                htmlElementOrNativeElement.removeEventListener('blur', handleBlur);
                htmlElementOrNativeElement.removeEventListener('mouseover', htmlElementOrNativeElement.focus);
                htmlElementOrNativeElement.removeEventListener('mouseout', htmlElementOrNativeElement.blur);
                htmlElementOrNativeElement.removeEventListener('click', handleClick);
            }
        };
    }, []);

    const returnValue: IUseFocusReturnValue = {
        focusManager: focusableContext.focusManagerFacade,
        isFocused,
        focusableId,
        node,
        setFocusableElementRef,
        setComponentWillRender,
    };

    // __state is exposed for internal use only (focusable HOC) and should never be available to
    // consumers via intellisense

    Object.defineProperty(returnValue, '__state', {
        value: mutableState,
        writable: false,
        enumerable: true,
    });

    return returnValue;
};

const defaultOptions = {
    columns: FocusableNode.defaultOptions.columns,
    itemsLength: FocusableNode.defaultOptions.virtualContainerItemsLength,
    startIndex: FocusableNode.defaultOptions.virtualContainerStartIndex,
    virtualFocusedIndex: FocusableNode.defaultOptions.virtualFocusedIndex,
    onFocusWillChange: noop,
};

export {useFocus as default, defaultOptions};
